import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "459",
  "slug": "what-is-the-mollweide-projection",
  "title": "What Is the Mollweide Projection? The Map That Keeps Areas Honest",
  "excerpt": "The Mollweide projection squeezes the whole world into a tidy ellipse while keeping every country the right size. Here is how this 200-year-old map works and why scientists still reach for it.",
  "date": "2026-06-16",
  "category": "Geography",
  "image": "/blog-images/world.jpg",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "The Mollweide projection is an equal-area map that shows the entire world inside an ellipse twice as wide as it is tall. Introduced by Karl Mollweide in 1805, it preserves the relative size of every region at the cost of distorting shapes near the edges.",
  "content": [
    {
      "type": "paragraph",
      "text": "Every flat map of a round Earth has to cheat somewhere. You cannot peel a globe onto a sheet of paper without stretching, squashing, or tearing something. The Mollweide projection is one of the most elegant answers to that problem, and its priority is clear from the start: keep every country the correct relative size, even if their shapes have to bend."
    },
    {
      "type": "paragraph",
      "text": "You have almost certainly seen it without knowing its name. Whenever you spot a world map drawn inside a clean oval, with curved meridians sweeping from pole to pole, you are most likely looking at a Mollweide."
    },
    {
      "type": "heading",
      "text": "A Map From 1805"
    },
    {
      "type": "paragraph",
      "text": "The projection is named after Karl Brandan Mollweide, a German mathematician and astronomer who introduced it in 1805. His goal was to design a map that preserved area faithfully, making it a true equal-area projection. More than two centuries later, it remains a standard choice whenever honest proportions matter more than perfect outlines."
    },
    {
      "type": "heading",
      "text": "How the Mollweide Works"
    },
    {
      "type": "paragraph",
      "text": "The Mollweide is what cartographers call a pseudocylindrical projection. A few rules define its distinctive look."
    },
    {
      "type": "list",
      "items": [
        "The whole world fits inside an ellipse exactly twice as wide as it is tall, a clean two-to-one ratio.",
        "The equator is drawn as a straight horizontal line, and the central meridian as a straight vertical line.",
        "All other meridians are curved as ellipses, sweeping outward and meeting at the poles, which become single points.",
        "The parallels of latitude are straight horizontal lines, spaced closer together near the poles to keep every region the right area."
      ]
    },
    {
      "type": "heading",
      "text": "The Trade-Off"
    },
    {
      "type": "paragraph",
      "text": "No projection is free. By insisting that area be preserved everywhere, the Mollweide has to distort shape, and that distortion grows toward the outer edges of the ellipse. Landmasses near the left and right margins, and near the poles, get noticeably stretched and sheared. A country in the middle of the map looks close to its true shape; the same country near the rim would look squeezed."
    },
    {
      "type": "paragraph",
      "text": "This is the fundamental bargain of cartography. The Mercator projection preserves angles and local shapes but wildly inflates the size of high-latitude land. The Mollweide makes the opposite choice, keeping sizes truthful while letting shapes warp. Neither is wrong; they are tuned for different jobs."
    },
    {
      "type": "heading",
      "text": "Where It Gets Used"
    },
    {
      "type": "paragraph",
      "text": "Because it shows the relative size of regions honestly, the Mollweide is a favourite for thematic world maps, the kind that display data like population, climate zones, or vegetation, where exaggerating the area of any region would mislead the reader. It also has a second life in science: astronomers use Mollweide-style maps to display the entire sky on a single oval, including famous all-sky images of the cosmic microwave background."
    },
    {
      "type": "paragraph",
      "text": "It even underpins other maps. Goode’s homolosine projection, the familiar \"orange-peel\" world map that is interrupted into lobes, stitches the Mollweide together with another equal-area projection to minimize distortion across the continents."
    },
    {
      "type": "heading",
      "text": "The Equal-Area Family"
    },
    {
      "type": "paragraph",
      "text": "The Mollweide is one member of a whole family of equal-area maps, each making the same promise to keep sizes honest but solving the shape problem in a different way. Knowing the relatives helps you recognize the Mollweide and understand why someone would pick it."
    },
    {
      "type": "list",
      "items": [
        "Gall-Peters — a rectangular equal-area map that famously stretches landmasses into tall, narrow shapes.",
        "Sinusoidal — a pointed, leaf-shaped equal-area projection with strongly curved meridians.",
        "Equal Earth — a modern projection from 2018, designed to keep areas true while looking more natural and less distorted than Gall-Peters.",
        "Mollweide — the elliptical option, prized for its clean oval frame and balanced look."
      ]
    },
    {
      "type": "paragraph",
      "text": "Each of these is chosen for a particular audience. A textbook trying to correct misconceptions about the size of continents might reach for Gall-Peters or Equal Earth, while a scientist mapping data across the whole globe might prefer the tidy ellipse of the Mollweide. The common thread is the refusal to lie about how big places really are, even when that honesty forces the shapes near the edges to stretch and lean in ways the real continents never do."
    },
    {
      "type": "heading",
      "text": "Why Projections Are Worth Knowing"
    },
    {
      "type": "paragraph",
      "text": "Learning to recognize map projections changes how you read every map you encounter. Once you understand that the Mollweide trades shape for size and the Mercator trades size for shape, you stop taking any single map at face value and start asking what it is optimized to show."
    }
  ],
  "imageAlt": "Earth regional satellite composite with shaded relief",
  "imageCaption": "NASA Earth Observatory / MODIS, August 2004. Regional composite with shaded relief and bathymetry; it shows landforms, not political boundaries or current conditions.",
  "sources": [
    {
      "title": "NASA: Blue Marble Next Generation (image source)",
      "url": "https://science.nasa.gov/earth/earth-observatory/blue-marble-next-generation/"
    }
  ]
};
